/**
 * Home feed screen — the primary `Tabs` landing surface.
 *
 * Responsibilities:
 *  - Renders the social feed (story row, portfolio card, filter pills, post list).
 *  - Pulls posts from one of two backend routes depending on the active filter:
 *      • Trending  → GET /posts/trending
 *      • everything else → GET /posts/feed?feedType=...  (see FEED_TYPE_MAP)
 *  - Pulls the wallet portfolio total from GET /wallet/portfolio.
 *  - Per-post engagement (like / repost / bookmark) hits
 *    POST|DELETE /posts/:id/{like,repost,bookmark} with optimistic UI.
 *
 * The backend returns camelCase + Prisma `_count` envelopes; normalizePost()
 * flattens that into the snake_case `Post` shape the cards render from.
 *
 * BARE-RN port (P3-2): expo-router useRouter/useFocusEffect → react-navigation
 * useNavigation/useFocusEffect (typed nav per navigation/types.ts); router.push
 * paths → navigation.navigate(RouteName, params); expo-linear-gradient →
 * react-native-linear-gradient; @expo/vector-icons → local Icon shim. The
 * realtime/dedupe/optimistic feed logic is ported verbatim.
 */
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Image,
  ActivityIndicator,
  useWindowDimensions,
  Share,
  Alert,
  Modal,
  TextInput,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { navigate as navRef } from '../../navigation/navigationRef';
import RichPostText from '../../components/feed/RichPostText';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LinearGradient from 'react-native-linear-gradient';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  useAnimatedScrollHandler,
  interpolate,
  Extrapolation,
} from 'react-native-reanimated';
import { getMutedWords, containsMutedWord } from '../../services/mutedWords';
import { Ionicons, IoniconName } from '../../components/ui/Icon';
import { colors, spacing, radius, fonts, getLayout, typographyScale, colorTokens, shadowTokens } from '../../constants/theme';
import { api, joinCommunity } from '../../services/api';
import { SHOW_CRYPTO } from '../../lib/comingSoon';
import { tabularNums } from '../../lib/format';
import { communityIcon, communityCover, learnCover } from '../../lib/icons';
import ReportBlockSheet, { ReportTarget } from '../../components/moderation/ReportBlockSheet';
import { isBlocked, onBlockedChange, isMuted, onMutedChange } from '../../services/moderation';
import ParticleBackground from '../../components/ui/ParticleBackground';
import Asset3D, { Asset3DName } from '../../components/ui/Asset3D';
import { GlassCard, GlassBar } from '../../components/glass';
import LogoWordmark from '../../components/LogoWordmark';
import {
  seedEngagement,
  subscribe as subscribeEngagement,
  applyLike,
  applyRepost,
  applyBookmark,
  type PostEngagement,
} from '../../services/postEngagement';
import { startFeedEvents, onNewFeedPost } from '../../services/feedEvents';
import { onReconnect } from '../../services/socket';
import { setFollow, amIFollowing, subscribeCounts } from '../../services/socialGraph';
import { useTabBarClearance } from '../../components/nav';
import SignalsRow from '../../components/home/SignalsRow';
import StoryRow from '../../components/home/StoryRow';
import type { StatusNote } from '../../components/home/StatusBubbles';
import { getNotes, setNote as apiSetNote, clearNote as apiClearNote, type Note } from '../../services/notes';
import GetStartedCard from '../../components/home/GetStartedCard';
import LiveSessionBanner from '../../components/home/LiveSessionBanner';
import { openDrawer } from '../../components/nav/DrawerMenu';
import Safe from '../../components/Safe';
import type { RootStackParamList } from '../../navigation/types';

type Nav = NativeStackNavigationProp<RootStackParamList>;

// Reanimated-wrapped FlatList so the footer Signals strip can collapse against a
// live scroll offset (useAnimatedScrollHandler) without dropping a frame.
const AnimatedFlatList = Animated.createAnimatedComponent(FlatList<Post>);

// Responsive layout bucket (phone | tablet | desktop) threaded through the rails
// so their cards scale up on wide screens instead of leaving dead horizontal
// space. Phone stays exactly as before.
type Layout = ReturnType<typeof getLayout>;

// Rail-card width per breakpoint. On tablet/desktop the horizontal cards grow so
// the wider feed column reads intentionally (not a stretched phone).
function scaleWidth(base: number, layout: Layout): number {
  if (layout === 'desktop') return Math.round(base * 1.28);
  if (layout === 'tablet') return Math.round(base * 1.14);
  return base;
}

// Feed filter tabs shown as pills. Order matters — it's the display order.
const FILTERS = ['For You', 'Following', 'Trending', 'New'];

// Quick-action buttons on the portfolio card; each routes to a crypto surface.
const PORTFOLIO_ACTIONS: { icon: IoniconName; label: string }[] = [
  { icon: 'swap-horizontal', label: 'Swap' },
  { icon: 'wallet', label: 'Wallet' },
  { icon: 'bar-chart', label: 'Markets' },
];

// Client-side post shape consumed by PostCard. This is the *normalized* shape —
// the wire format from the API is different (see normalizePost).
interface Post {
  id: string;
  content: string;
  created_at: string;
  likes_count: number;
  comments_count: number;
  reposts_count: number;
  liked?: boolean;
  bookmarked?: boolean;
  reposted?: boolean;
  // Optional in-feed media (image URLs). undefined when the post has none, so a
  // text-only post never renders an empty image frame. Rendering is v2-gated
  // (HAS_POST_MEDIA) until the backend media contract is confirmed.
  media?: string[];
  user: {
    id?: string;
    display_name: string;
    username: string;
    avatar_url?: string;
  };
}

// A failed like/repost/bookmark rolls back optimistically — but a silent
// bounce-back reads as a glitch. Surface ONE lightweight, non-spammy Alert on
// rollback (skip the silent auth-expiry sentinels, which route to Login).
function notifyActionFailed(res: { silent?: boolean }) {
  if (res?.silent) return;
  Alert.alert('Something went wrong', 'That action didn’t go through. Please try again.');
}

// v2 gate: the backend feed media contract (raw.media/images/attachments) is not
// yet confirmed, so we DON'T ship an image surface that might render an empty or
// broken frame. Flip to true once the API is verified to return image URLs.
const HAS_POST_MEDIA = false;

// Read image URLs from whichever media field the wire uses. Returns undefined
// when there are none (never an empty array) so PostCard can cheaply skip the
// media block. Only string http(s) URLs survive.
function normalizeMedia(raw: any): string[] | undefined {
  const src = raw?.media ?? raw?.images ?? raw?.attachments;
  if (!Array.isArray(src)) return undefined;
  const urls = src
    .map((m: any) => (typeof m === 'string' ? m : m?.url ?? m?.uri ?? m?.src))
    .filter((u: any): u is string => typeof u === 'string' && /^https?:\/\//.test(u));
  return urls.length > 0 ? urls : undefined;
}

// Backend post shape: { id, content, createdAt, repostCount, User:{username,
// displayName,avatar}, _count:{Vote,Comment} }. Normalize to the client `Post`
// shape the PostCard already reads.
function normalizePost(raw: any): Post {
  // Two server shapes flow in: the FEED-LIST shape (`User` relation + `_count` +
  // top-level flags) AND the CREATE/`feed:post` fan-out envelope (`author` +
  // `stats` + `userInteractions`). Read both so a live-prepended post from a
  // followed author renders identically to a refetched one (not anonymous/zeroed).
  const u = (raw && typeof raw === 'object' && (raw.User || raw.author || raw.user)) || {};
  const stats = raw?.stats || {};
  const ui = raw?.userInteractions || {};
  // Safe user defaults: username always a non-empty string, display_name falls
  // back to username, avatar_url only kept when it's a non-empty string (so a
  // null/number/object from the wire never reaches <Image source={{ uri }}>).
  const username =
    typeof u.username === 'string' && u.username.length > 0 ? u.username : 'user';
  const rawDisplay = u.displayName ?? u.display_name;
  const display_name =
    typeof rawDisplay === 'string' && rawDisplay.length > 0 ? rawDisplay : username;
  const rawAvatar = u.avatar ?? u.avatar_url ?? u.avatarUrl;
  const avatar_url =
    typeof rawAvatar === 'string' && rawAvatar.length > 0 ? rawAvatar : undefined;
  const rawId = u.id ?? raw?.userId;
  return {
    id: raw?.id,
    content: raw?.content ?? '',
    created_at: raw?.createdAt ?? raw?.created_at ?? '',
    likes_count: raw?._count?.Vote ?? raw?.upvotes ?? stats?.likes ?? 0,
    comments_count: raw?._count?.Comment ?? raw?.commentCount ?? stats?.comments ?? 0,
    reposts_count: raw?.repostCount ?? stats?.reposts ?? 0,
    liked: raw?.liked ?? ui?.liked ?? false,
    bookmarked: raw?.bookmarked ?? ui?.bookmarked ?? false,
    reposted: raw?.reposted ?? ui?.reposted ?? false,
    media: normalizeMedia(raw),
    user: {
      id: rawId != null ? String(rawId) : undefined,
      display_name,
      username,
      avatar_url,
    },
  };
}

// Compact relative-time formatter ("now" / "5m" / "3h" / "2d"). Returns ''
// for missing or unparseable dates so the UI degrades quietly.
function timeAgo(dateStr?: string | null): string {
  if (!dateStr) return '';
  const ts = new Date(dateStr).getTime();
  if (isNaN(ts)) return '';
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

// Wire shape of GET /stories — grouped per user (see api stories.service.ts
// GroupedStories). Only the fields the row needs; the viewer refetches its own.
interface StoryGroup {
  userId: string;
  username?: string;
  displayName?: string;
  avatar?: string | null;
  hasUnviewed?: boolean;
  stories?: { id: string }[];
}

// StoryRow now lives in components/home/StoryRow.tsx (a drop-in with the same
// prop shape plus optional IG-Notes bubbles). It is imported at the top of this
// file; the old inline copy was removed to avoid a duplicate identifier.

// USD formatter for the portfolio header; renders an em-dash when value is
// unknown (null) rather than "$0.00", which would imply an empty wallet.
function formatUsd(value: number | null): string {
  if (value == null) return '—';
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function PortfolioCard({ onAction, value }: { onAction: (label: string) => void; value: number | null }) {
  return (
    <GlassCard mode="light" padding={spacing.lg} radius={radius.lg} style={styles.portfolio}>
      <View style={styles.portfolioLabelRow}>
        <Text style={styles.portfolioLabel}>Portfolio Value</Text>
        <TouchableOpacity
          onPress={() => Alert.alert('Portfolio Value', 'Your total crypto portfolio value across all connected wallets and exchanges.')}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          accessibilityRole="button"
          accessibilityLabel="About portfolio value"
        >
          <Ionicons name="help-circle-outline" size={18} color={colors.muted} />
        </TouchableOpacity>
      </View>
      {/* Value opens the full Portfolio surface (Coins|NFTs, v2-gated wallet). */}
      <TouchableOpacity onPress={() => onAction('Portfolio')} accessibilityRole="button" accessibilityLabel="Open portfolio">
        <Text style={[styles.portfolioValue, tabularNums]}>{formatUsd(value)}</Text>
      </TouchableOpacity>
      <View style={styles.portfolioActions}>
        {PORTFOLIO_ACTIONS.map((a) => (
          <TouchableOpacity
            key={a.label}
            style={styles.portfolioAction}
            onPress={() => onAction(a.label)}
            accessibilityRole="button"
            accessibilityLabel={a.label}
          >
            <View style={styles.portfolioIconWrap}>
              <Ionicons name={a.icon} size={18} color="#FFFFFF" />
            </View>
            <Text style={styles.portfolioActionLabel}>{a.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </GlassCard>
  );
}

// ── Discover-communities carousel ─────────────────────────────────────────────
// Horizontal rail of communities pulled from GET /communities (live). Each card
// deep-links into that community. Renders null when the list is empty/errors so
// it never shows a broken rail.
interface CarouselCommunity {
  id: string;
  name: string;
  displayName?: string;
  avatar?: string;
  memberCount?: number;
  joined?: boolean;
}
function CommunitiesCarousel({
  communities,
  onOpen,
  onSeeAll,
  layout = 'phone',
}: {
  communities: CarouselCommunity[];
  onOpen: (name: string) => void;
  onSeeAll: () => void;
  layout?: Layout;
}) {
  if (!Array.isArray(communities) || communities.length === 0) return null;
  const cardWidth = scaleWidth(128, layout);
  return (
    <View style={styles.railSection}>
      <View style={styles.railHeader}>
        <View style={styles.railHeaderLeft}>
          <Ionicons name="planet" size={16} color={colors.primary} />
          <Text style={styles.railTitle}>Discover communities</Text>
        </View>
        <TouchableOpacity
          onPress={onSeeAll}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityRole="button"
          accessibilityLabel="See all communities"
        >
          <Text style={styles.railSeeAll}>See all</Text>
        </TouchableOpacity>
      </View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.railRow}
      >
        {communities.map((c) => {
          const title = c.displayName || c.name;
          const initial = (title || 'C').charAt(0).toUpperCase();
          return (
            <GlassCard
              key={c.id || c.name}
              padding={spacing.md}
              radius={radius.lg}
              onPress={() => onOpen(c.name)}
              style={[styles.communityCard, { width: cardWidth }]}
              accessibilityLabel={`${title} community${c.memberCount != null ? `, ${c.memberCount} members` : ''}`}
            >
              {communityIcon(c.name, c.displayName, c.avatar) ? (
                <Image source={{ uri: communityIcon(c.name, c.displayName, c.avatar) }} style={styles.communityAvatar} />
              ) : (
                <View style={[styles.communityAvatar, styles.communityAvatarFallback]}>
                  <Text style={styles.communityInitial}>{initial}</Text>
                </View>
              )}
              <Text style={styles.communityName} numberOfLines={1}>{title}</Text>
              <Text style={[styles.communityMeta, tabularNums]} numberOfLines={1}>
                {c.memberCount != null ? `${c.memberCount.toLocaleString()} members` : 'Community'}
              </Text>
            </GlassCard>
          );
        })}
      </ScrollView>
    </View>
  );
}

// ── "Communities to join" strip (x.com-style horizontal chip row) ─────────────
// Replaces the STORIES circle row at the top of the feed (client's explicit ask).
// Each rounded glass chip shows a community icon + name + a small inline Join
// button; the chip body taps into the Community, the Join button follows it
// (POST /communities/:name/join) optimistically without navigating away. Renders
// nothing when there are no communities so the header never shows an empty strip.
function JoinCommunityChip({
  community,
  onOpen,
}: {
  community: CarouselCommunity;
  onOpen: (name: string) => void;
}) {
  const title = community.displayName || community.name;
  // Reflect real membership so a community you're ALREADY in doesn't show "Join".
  const [joined, setJoined] = useState(!!community.joined);
  const [busy, setBusy] = useState(false);
  // Re-sync when the carousel refetches (pull-to-refresh / focus): otherwise the
  // chip keeps its first-mount value and shows a stale "Join"/"Joined".
  useEffect(() => { setJoined(!!community.joined); }, [community.joined]);
  const handleJoin = useCallback(async () => {
    if (busy || joined) return;
    setBusy(true);
    setJoined(true); // optimistic
    const res = await joinCommunity(community.name);
    if (!res.success) setJoined(false); // rollback
    setBusy(false);
  }, [busy, joined, community.name]);
  return (
    <GlassCard
      padding={0}
      radius={radius.lg}
      onPress={() => onOpen(community.name)}
      style={styles.joinCard}
      accessibilityLabel={`${title} community`}
    >
      {/* Circular community avatar (client ask: circles, not square covers) —
          centered, with the name + Join stacked beneath for a clean social look. */}
      <View style={styles.joinCardBody}>
        <Image
          source={{ uri: communityCover(community.name, community.displayName, community.avatar) }}
          style={styles.joinCardAvatar}
          resizeMode="cover"
        />
        <Text style={styles.joinCardName} numberOfLines={1}>{title}</Text>
        <TouchableOpacity
          style={[styles.joinCardBtn, joined && styles.joinCardBtnActive]}
          onPress={handleJoin}
          disabled={busy || joined}
          accessibilityRole="button"
          accessibilityLabel={joined ? `Joined ${title}` : `Join ${title}`}
        >
          <Text style={[styles.joinChipBtnText, joined && styles.joinChipBtnTextActive]}>
            {joined ? 'Joined' : 'Join'}
          </Text>
        </TouchableOpacity>
      </View>
    </GlassCard>
  );
}

function JoinCommunitiesRow({
  communities,
  onOpen,
  onSeeAll,
}: {
  communities: CarouselCommunity[];
  onOpen: (name: string) => void;
  onSeeAll: () => void;
}) {
  if (!Array.isArray(communities) || communities.length === 0) return null;
  return (
    <View style={styles.joinRowSection}>
      <View style={styles.joinRowHeader}>
        <View style={styles.railHeaderLeft}>
          <Ionicons name="people" size={15} color={colors.primary} />
          <Text style={styles.joinRowTitle}>Communities to join</Text>
        </View>
        <TouchableOpacity
          onPress={onSeeAll}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityRole="button"
          accessibilityLabel="See all communities"
        >
          <Text style={styles.railSeeAll}>See all</Text>
        </TouchableOpacity>
      </View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.joinRow}
      >
        {communities.map((c) => (
          <JoinCommunityChip key={c.id || c.name} community={c} onOpen={onOpen} />
        ))}
      </ScrollView>
    </View>
  );
}

// ── Pinned announcement / live bar (x.com pinned promoted row) ────────────────

// ── Learn-preview rail ────────────────────────────────────────────────────────
// Horizontal rail of learn articles from GET /learn (live). Opens LearnArticle.
// Renders null when empty so the feed never shows an empty rail.
interface LearnPreview {
  id?: string;
  slug?: string;
  title?: string;
  category?: string;
  difficulty?: string;
  image?: string;
  imageUrl?: string;
}
function LearnRail({
  articles,
  onOpen,
  onSeeAll,
  layout = 'phone',
}: {
  articles: LearnPreview[];
  onOpen: (slug: string) => void;
  onSeeAll: () => void;
  layout?: Layout;
}) {
  const items = (Array.isArray(articles) ? articles : []).filter((a) => a.slug).slice(0, 8);
  if (items.length === 0) return null;
  const cardWidth = scaleWidth(200, layout);
  const imageHeight = layout === 'phone' ? 96 : Math.round((cardWidth * 96) / 200);
  return (
    <View style={styles.railSection}>
      <View style={styles.railHeader}>
        <View style={styles.railHeaderLeft}>
          <Ionicons name="school" size={16} color={colors.primary} />
          <Text style={styles.railTitle}>Learn crypto</Text>
        </View>
        <TouchableOpacity
          onPress={onSeeAll}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityRole="button"
          accessibilityLabel="Open the Learning Center"
        >
          <Text style={styles.railSeeAll}>See all</Text>
        </TouchableOpacity>
      </View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.railRow}
      >
        {items.map((a, i) => {
          // #44: always a real featured image (article's own, else a stable
          // topic-themed photo) so the Learn rail never shows a blank card.
          const img = learnCover(a.title, a.slug, a.image || a.imageUrl, 600);
          return (
            <GlassCard
              key={a.slug || a.id || String(i)}
              padding={0}
              radius={radius.lg}
              onPress={() => a.slug && onOpen(a.slug)}
              style={[styles.learnCard, { width: cardWidth }]}
              accessibilityLabel={`Learn: ${a.title || 'Article'}`}
            >
              {img ? (
                <Image source={{ uri: img }} style={[styles.learnImage, { height: imageHeight }]} />
              ) : (
                <View style={[styles.learnImage, styles.learnImageFallback, { height: imageHeight }]}>
                  <Ionicons name="book-outline" size={26} color={colors.primary} />
                </View>
              )}
              <View style={styles.learnBody}>
                {!!a.category && (
                  <Text style={styles.learnCategory} numberOfLines={1}>
                    {a.category.toUpperCase()}
                  </Text>
                )}
                <Text style={styles.learnTitle} numberOfLines={2}>
                  {a.title || 'Untitled'}
                </Text>
              </View>
            </GlassCard>
          );
        })}
      </ScrollView>
    </View>
  );
}

// ── Discovery body (empty-feed) types ─────────────────────────────────────────
// A person suggested in "Who to follow" (from GET /trending → users). A hashtag
// harvested from trending posts for the "Trending topics" chips.
interface SuggestedUser {
  id: string;
  username: string;
  displayName: string;
  avatar?: string;
  bio?: string;
}
interface TrendingTopic {
  tag: string;
  posts: number;
}

// ── "Start a conversation" prompt ─────────────────────────────────────────────
// A composer-launcher card that leads the empty-feed discovery body — the app's
// primary create action, so the timeline never reads as "there's nothing here."
function StartConversationCard({
  avatarUrl,
  onPress,
}: {
  avatarUrl?: string;
  onPress: () => void;
}) {
  return (
    <GlassCard
      padding={spacing.lg}
      radius={radius.lg}
      onPress={onPress}
      style={styles.composerCard}
      accessibilityLabel="Start a conversation"
    >
      {avatarUrl && avatarUrl.length > 0 ? (
        <Image source={{ uri: avatarUrl }} style={styles.composerAvatar} />
      ) : (
        <View style={[styles.composerAvatar, styles.composerAvatarFallback]}>
          <Ionicons name="person" size={18} color={colors.primary} />
        </View>
      )}
      <Text style={styles.composerPlaceholder} numberOfLines={1}>
        Start a conversation…
      </Text>
      <View style={styles.composerBtn}>
        <Ionicons name="create-outline" size={18} color="#FFFFFF" />
      </View>
    </GlassCard>
  );
}

// ── "Who to follow" suggestion row ────────────────────────────────────────────
// One person with an inline Follow button. Follow state is read from the SHARED
// socialGraph store (amIFollowing) — the SAME source the profile screen reads —
// so following here reflects on the profile and vice-versa. Toggling goes through
// setFollow (optimistic); on a server rejection we ROLL BACK and surface the real
// error inline (e.g. "Already following") instead of failing silently. We also
// subscribe (subscribeCounts) so a follow/unfollow made on ANOTHER screen flips
// this button live. Tapping the row opens their profile.
function SuggestedUserRow({
  user,
  onOpen,
}: {
  user: SuggestedUser;
  onOpen: (username: string) => void;
}) {
  const [following, setFollowing] = useState(() =>
    amIFollowing({ id: user.id, username: user.username })
  );
  const [busy, setBusy] = useState(false);
  // Inline error (real server message, e.g. "Already following") shown briefly
  // under the row so a rejected toggle never fails silently.
  const [followErr, setFollowErr] = useState<string | null>(null);

  // Live sync: reflect follow/unfollow made on other screens (profile, etc.).
  // subscribeCounts fires with the shared record (which carries isFollowing when
  // setFollow reconciled it); we prefer that flag but fall back to re-reading the
  // authoritative local truth from the store so this button never drifts.
  useEffect(() => {
    const unsub = subscribeCounts({ id: user.id, username: user.username }, (c) => {
      const next =
        typeof c.isFollowing === 'boolean'
          ? c.isFollowing
          : amIFollowing({ id: user.id, username: user.username });
      setFollowing((prev) => (prev === next ? prev : next));
    });
    // Re-affirm from the store on mount (covers a follow made before this row rendered).
    setFollowing(amIFollowing({ id: user.id, username: user.username }));
    return unsub;
  }, [user.id, user.username]);

  const handleFollow = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setFollowErr(null);
    const was = amIFollowing({ id: user.id, username: user.username });
    const next = !was;
    setFollowing(next); // optimistic
    const res = await setFollow({ id: user.id, username: user.username }, next);
    if (!res.success) {
      setFollowing(was); // rollback to the pre-toggle truth
      // Surface the REAL error (esp. "Already following") — inline + Alert.
      const msg = res.error || (next ? 'Could not follow. Please try again.' : 'Could not unfollow. Please try again.');
      setFollowErr(msg);
      Alert.alert(next ? 'Follow failed' : 'Unfollow failed', msg);
    }
    setBusy(false);
  }, [busy, user.id, user.username]);

  const initial = (user.displayName || user.username || 'U').charAt(0).toUpperCase();
  return (
    <View>
      <TouchableOpacity
        activeOpacity={0.7}
        style={styles.suggestRow}
        onPress={() => onOpen(user.username)}
        accessibilityRole="button"
        accessibilityLabel={`Open ${user.displayName}'s profile`}
      >
        {user.avatar ? (
          <Image source={{ uri: user.avatar }} style={styles.suggestAvatar} />
        ) : (
          <View style={[styles.suggestAvatar, styles.suggestAvatarFallback]}>
            <Text style={styles.suggestInitial}>{initial}</Text>
          </View>
        )}
        <View style={styles.suggestMeta}>
          <Text style={styles.suggestName} numberOfLines={1}>{user.displayName}</Text>
          <Text style={styles.suggestHandle} numberOfLines={1}>
            {user.bio ? user.bio : `@${user.username}`}
          </Text>
        </View>
        <TouchableOpacity
          style={[styles.followBtn, following && styles.followBtnActive]}
          onPress={handleFollow}
          disabled={busy}
          accessibilityRole="button"
          accessibilityLabel={following ? `Unfollow ${user.username}` : `Follow ${user.username}`}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Text style={[styles.followBtnText, following && styles.followBtnTextActive]}>
            {following ? 'Following' : 'Follow'}
          </Text>
        </TouchableOpacity>
      </TouchableOpacity>
      {!!followErr && (
        <Text style={styles.followError} numberOfLines={2}>{followErr}</Text>
      )}
    </View>
  );
}

// ── Clean, tab-aware empty state ──────────────────────────────────────────────
// One icon + one line + one CTA (NN/g + Apple HIG: never a dead end, never
// onboarding/connect rows inside a content tab). Replaces the old DiscoveryBody
// fallback, which read as a bad "connect" onboarding surface on the Trending tab.
function FeedEmptyState({ filter, onPrimary }: { filter: string; onPrimary: () => void }) {
  const copy: Record<string, { asset: Asset3DName; title: string; sub: string; cta: string }> = {
    'For You': { asset: 'emptyCluster', title: 'Your feed is warming up', sub: 'Post something or follow a few people and your For You feed fills in fast.', cta: 'Create a post' },
    Following: { asset: 'community', title: 'Nothing here yet', sub: 'Follow people and communities to see their posts in Following.', cta: 'Find people' },
    Trending: { asset: 'coin', title: 'No trending posts right now', sub: 'Check back soon — trending updates as the community gets active.', cta: 'Refresh' },
    New: { asset: 'orb', title: 'No new posts yet', sub: 'Fresh posts will show up here as they’re published.', cta: 'Refresh' },
  };
  const c = copy[filter] ?? copy['For You'];
  return (
    <View style={styles.emptyCard}>
      <Asset3D name={c.asset} size={132} style={{ marginBottom: spacing.md }} />
      <Text style={styles.emptyText}>{c.title}</Text>
      <Text style={styles.emptySubtext}>{c.sub}</Text>
      <TouchableOpacity style={styles.emptyCtaBtn} onPress={onPrimary} activeOpacity={0.85} accessibilityRole="button" accessibilityLabel={c.cta}>
        <Text style={styles.emptyCtaText}>{c.cta}</Text>
      </TouchableOpacity>
    </View>
  );
}


// x.com-style sub-header: an equal-width underline segmented control that pins
// beneath the floating header (skill 7 — reuses ProfileScreen's underline-tab
// pattern for cross-screen familiarity). The active indicator is a single 2px
// bar that SLIDES between tabs via a reanimated shared value (skill 3-4:
// interruptible spring, damping ~1.0, no bounce) instead of hard-cutting.
function SegmentedTabs({
  active,
  onSelect,
}: {
  active: string;
  onSelect: (f: string) => void;
}) {
  const [rowWidth, setRowWidth] = useState(0);
  const tabWidth = rowWidth > 0 ? rowWidth / FILTERS.length : 0;
  const activeIndex = Math.max(0, FILTERS.indexOf(active));
  const tx = useSharedValue(0);
  useEffect(() => {
    // Spring the underline to the active tab. damping ~1.0 → smooth settle, no
    // overshoot; the value re-targets live so rapid tab taps stay continuous.
    tx.value = withSpring(activeIndex * tabWidth, { damping: 20, stiffness: 200, mass: 0.6 });
  }, [activeIndex, tabWidth, tx]);
  const indicatorStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: tx.value }],
  }));
  return (
    <View
      style={styles.segTabs}
      onLayout={(e) => setRowWidth(e.nativeEvent.layout.width)}
    >
      {FILTERS.map((f) => {
        const isActive = active === f;
        return (
          <TouchableOpacity
            key={f}
            style={styles.segTab}
            onPress={() => onSelect(f)}
            accessibilityRole="button"
            accessibilityState={{ selected: isActive }}
            accessibilityLabel={f}
            activeOpacity={0.7}
          >
            <Text style={[styles.segTabLabel, isActive && styles.segTabLabelActive]}>
              {f}
            </Text>
          </TouchableOpacity>
        );
      })}
      {tabWidth > 0 && (
        <Animated.View
          pointerEvents="none"
          style={[styles.segIndicator, { width: tabWidth }, indicatorStyle]}
        />
      )}
    </View>
  );
}

// Single feed post. Engagement (like/repost/bookmark + counts) is read from the
// SHARED postEngagement store so the feed and the post-detail screen never drift,
// and live socket echoes (post:like / post:repost to the author's room) reconcile
// counts here too. The *Ref flags coalesce rapid taps. Memoized (React.memo +
// stable callbacks) so one card's update never re-renders the whole list.
function PostCardBase({ post, onPress, onOptions }: { post: Post; onPress: (id: string) => void; onOptions?: (post: Post) => void }) {
  const user = post.user || { display_name: 'User', username: 'user' };

  // Bind the parent's STABLE callbacks against this card's post once. renderItem
  // can therefore pass the parent callbacks straight through (no fresh closures
  // per render), so the memo comparator below actually holds and a sibling card's
  // update — or a live prepend — never re-renders this row.
  const handlePress = useCallback(() => onPress(post.id), [onPress, post.id]);
  const handleOptions = useCallback(() => onOptions?.(post), [onOptions, post]);

  // Seed the store from this post's server counts (preserves any existing
  // optimistic/live state for the same id) and subscribe for live updates.
  const [eng, setEng] = useState<PostEngagement>(() =>
    seedEngagement(post.id, {
      liked: post.liked ?? false,
      likes: post.likes_count ?? 0,
      reposted: post.reposted ?? false,
      reposts: post.reposts_count ?? 0,
      bookmarked: post.bookmarked ?? false,
    })
  );
  useEffect(() => {
    // Re-seed if the post object identity changes (e.g. refetch) then subscribe.
    setEng(
      seedEngagement(post.id, {
        liked: post.liked ?? false,
        likes: post.likes_count ?? 0,
        reposted: post.reposted ?? false,
        reposts: post.reposts_count ?? 0,
        bookmarked: post.bookmarked ?? false,
      })
    );
    return subscribeEngagement(post.id, setEng);
  }, [post.id, post.likes_count, post.reposts_count, post.liked, post.reposted, post.bookmarked]);

  const { liked, likes: likesCount, reposted, reposts: repostsCount, bookmarked } = eng;
  const likingRef = useRef(false);
  const repostingRef = useRef(false);
  const bookmarkingRef = useRef(false);

  // Optimistic like via the shared store (rollback on failure). DELETE removes
  // the like, POST adds it — chosen from the prior (wasLiked) state.
  const handleLike = useCallback(async () => {
    if (likingRef.current) return;
    likingRef.current = true;
    const wasLiked = eng.liked;
    const rollback = applyLike(post.id, !wasLiked);
    const res = await api(`/posts/${post.id}/like`, { method: wasLiked ? 'DELETE' : 'POST' });
    if (!res.success) { rollback(); notifyActionFailed(res); }
    likingRef.current = false;
  }, [post.id, eng.liked]);

  // Optimistic repost, same pattern.
  const handleRepost = useCallback(async () => {
    if (repostingRef.current) return;
    repostingRef.current = true;
    const wasReposted = eng.reposted;
    const rollback = applyRepost(post.id, !wasReposted);
    const res = await api(`/posts/${post.id}/repost`, { method: wasReposted ? 'DELETE' : 'POST' });
    if (!res.success) { rollback(); notifyActionFailed(res); }
    repostingRef.current = false;
  }, [post.id, eng.reposted]);

  // Optimistic bookmark toggle (no counter).
  const handleBookmark = useCallback(async () => {
    if (bookmarkingRef.current) return;
    bookmarkingRef.current = true;
    const wasBookmarked = eng.bookmarked;
    const rollback = applyBookmark(post.id, !wasBookmarked);
    const res = await api(`/posts/${post.id}/bookmark`, { method: wasBookmarked ? 'DELETE' : 'POST' });
    if (!res.success) { rollback(); notifyActionFailed(res); }
    bookmarkingRef.current = false;
  }, [post.id, eng.bookmarked]);

  return (
    <TouchableOpacity
      activeOpacity={0.7}
      onPress={handlePress}
      style={styles.postCard}
      accessibilityRole="button"
      accessibilityLabel={`Post by ${user.display_name || user.username || 'user'}`}
    >
      <View style={styles.postHeader}>
        <View style={styles.postAvatarWrap}>
          {user.avatar_url && user.avatar_url.length > 0 ? (
            <Image source={{ uri: user.avatar_url }} style={styles.postAvatar} />
          ) : (
            <View style={[styles.postAvatar, styles.postAvatarFallback]}>
              <Text style={styles.postAvatarLetter}>
                {(user.display_name || user.username || 'U').charAt(0).toUpperCase()}
              </Text>
            </View>
          )}
        </View>
        <View style={styles.postMeta}>
          <Text style={styles.postName} numberOfLines={1}>
            {user.display_name || user.username || 'User'}
          </Text>
          <Text style={styles.postHandle} numberOfLines={1}>
            @{user.username} · {timeAgo(post.created_at)}
          </Text>
        </View>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel="Post options"
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          onPress={handleOptions}
        >
          <Ionicons name="ellipsis-horizontal" size={18} color={colors.muted} />
        </TouchableOpacity>
      </View>
      {!!post.content && (
        <RichPostText
          content={post.content}
          style={styles.postContent}
          collapsedLines={6}
          onCashtag={(symbol) => navRef('CoinDetail', { symbol })}
        />
      )}
      {/* In-post media, centered (v2-gated). Single image → one contained 16:9
          card; multiple → a centered 2-up grid. Never renders an empty frame:
          gated by HAS_POST_MEDIA and by media presence. */}
      {HAS_POST_MEDIA && post.media && post.media.length > 0 && (
        <View style={styles.postMediaWrap}>
          {post.media.length === 1 ? (
            <Image
              source={{ uri: post.media[0] }}
              style={styles.postMediaSingle}
              resizeMode="cover"
            />
          ) : (
            <View style={styles.postMediaGrid}>
              {post.media.slice(0, 4).map((uri, i) => (
                <Image
                  key={`${uri}-${i}`}
                  source={{ uri }}
                  style={styles.postMediaCell}
                  resizeMode="cover"
                />
              ))}
            </View>
          )}
        </View>
      )}
      <View style={styles.postActions}>
        {/* Primary actions grouped left (X/Twitter-style) — reads far better than
            5 icons stretched edge-to-edge. Save + share pushed to the right. */}
        <View style={styles.postActionsGroup}>
          <TouchableOpacity
            style={styles.postActionBtn}
            onPress={handleLike}
            accessibilityRole="button"
            accessibilityLabel={liked ? 'Unlike post' : 'Like post'}
          >
            <Ionicons name={liked ? 'heart' : 'heart-outline'} size={18} color={liked ? colors.error : colors.muted} />
            {likesCount > 0 && (
              <Text style={[styles.postActionCount, tabularNums, liked && { color: colors.error }]}>{likesCount}</Text>
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.postActionBtn}
            onPress={handlePress}
            accessibilityRole="button"
            accessibilityLabel="View comments"
          >
            <Ionicons name="chatbubble-outline" size={17} color={colors.muted} />
            {(post.comments_count ?? 0) > 0 && (
              <Text style={[styles.postActionCount, tabularNums]}>{post.comments_count}</Text>
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.postActionBtn}
            onPress={handleRepost}
            accessibilityRole="button"
            accessibilityLabel={reposted ? 'Undo repost' : 'Repost'}
          >
            <Ionicons name="repeat-outline" size={19} color={reposted ? colors.primary : colors.muted} />
            {repostsCount > 0 && (
              <Text style={[styles.postActionCount, tabularNums, reposted && { color: colors.primary }]}>{repostsCount}</Text>
            )}
          </TouchableOpacity>
        </View>
        <View style={styles.postActionsGroup}>
          <TouchableOpacity
            style={styles.postActionBtn}
            onPress={handleBookmark}
            accessibilityRole="button"
            accessibilityLabel={bookmarked ? 'Remove bookmark' : 'Bookmark post'}
          >
            <Ionicons name={bookmarked ? 'bookmark' : 'bookmark-outline'} size={17} color={bookmarked ? colors.primary : colors.muted} />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.postActionBtn}
            onPress={() => { Share.share({ message: `Check this on CRYB: https://platform.cryb.ai/post/${post.id}` }).catch(() => {}); }}
            accessibilityRole="button"
            accessibilityLabel="Share post"
          >
            <Ionicons name="share-outline" size={17} color={colors.muted} />
          </TouchableOpacity>
        </View>
      </View>
    </TouchableOpacity>
  );
}

// Memoized so one card's engagement update (or a sibling's) never re-renders the
// whole feed. Re-renders only when this post's identity/content or its callbacks
// change; live like/repost counts come through the engagement-store subscription
// inside the card, not through prop changes.
const PostCard = memo(
  PostCardBase,
  (prev, next) =>
    prev.post.id === next.post.id &&
    prev.post.content === next.post.content &&
    prev.post.comments_count === next.post.comments_count &&
    (prev.post.media?.length ?? 0) === (next.post.media?.length ?? 0) &&
    prev.onPress === next.onPress &&
    prev.onOptions === next.onOptions
);

// Per-item error boundary: if ONE malformed post object throws during render,
// only that row disappears — the feed (and the whole screen) never blanks.
// Class component because error boundaries can't be hooks.
class PostErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(err: unknown) {
    // Log-and-swallow: a bad post must never take the screen down.
    console.warn('[home] post render failed, skipping row', err);
  }
  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

// For You / Following / New map to GET /posts/feed feedType values; Trending
// uses the dedicated GET /posts/trending route (score+engagement+recency).
// Both routes return { data: { items: [...] } } with the same post shape.
// Module-scoped: stable identity, never re-created per render.
const FEED_TYPE_MAP: Record<string, string> = {
  'For You': 'algorithmic',
  'Following': 'following',
  'New': 'chronological',
};

// Auto-retry budget for a failed feed fetch (transient network / cold-start
// auth race / brief 5xx). After MAX_FEED_RETRIES the retryable error state
// shows; the Retry button starts a fresh attempt cycle.
const MAX_FEED_RETRIES = 2;
const FEED_RETRY_BASE_MS = 1200;
// UI watchdog: never show the full-screen spinner longer than this — flip to
// the friendly error + Retry instead (a late-succeeding fetch still lands).
const FEED_LOADING_WATCHDOG_MS = 10000;

export default function HomeScreen() {
  // ── Layout / navigation ──
  const navigation = useNavigation<Nav>();
  const { width } = useWindowDimensions();
  // Safe-area top inset: the Tabs navigator has headerShown:false, so this
  // screen owns its own header and MUST pad below the iOS status bar (the
  // June-27 regression rendered the CRYB wordmark under the clock).
  const insets = useSafeAreaInsets();
  // Exact bottom padding to clear the floating glass tab bar on THIS device
  // (safe-area-aware; replaces the flat worst-case token).
  const tabBarClearance = useTabBarClearance(spacing.lg);
  const layout = getLayout(width);
  // Cap the feed width on tablet/desktop so the single-column layout stays
  // readable + centered (no edge-to-edge line lengths), while the wider column
  // lets the rail cards breathe. Phone is uncapped (full-bleed as before).
  const maxContentWidth = layout === 'desktop' ? 720 : layout === 'tablet' ? 600 : undefined;

  // ── State ──
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  // Ref mirror of `loading` so the watchdog timer can read the live value
  // without being re-armed on every state change.
  const loadingRef = useRef(true);
  const [error, setError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // ── Pagination (page-based; the /feed + /trending endpoints return
  // { items, page, hasMore }). `page` is the last page loaded, `hasMore` gates
  // onEndReached, `loadingMore` drives the footer spinner. All reset on a filter
  // switch / refresh so paging always starts fresh for the visible tab.
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Guard so overlapping onEndReached / momentum callbacks can't double-fetch a page.
  const loadingMoreRef = useRef(false);
  const [activeFilter, setActiveFilter] = useState('For You');
  // Current user info for the story row avatar
  const [meAvatar, setMeAvatar] = useState<string | undefined>(undefined);
  const [meDisplayName, setMeDisplayName] = useState<string | undefined>(undefined);
  // Current user's id/username — gate the author-only "Delete post" kebab action.
  const [meId, setMeId] = useState<string | undefined>(undefined);
  const [meUsername, setMeUsername] = useState<string | undefined>(undefined);
  // Guard so a double-confirmed delete can't fire DELETE twice for the same post.
  const deletingPostRef = useRef<Record<string, boolean>>({});
  // Muted words (device-local). Reloaded on focus so edits in Settings apply
  // immediately on return. Posts containing any muted word are hidden client-side.
  const [mutedWords, setMutedWords] = useState<string[]>([]);
  useFocusEffect(
    useCallback(() => {
      let active = true;
      getMutedWords().then((w) => { if (active) setMutedWords(w); });
      return () => { active = false; };
    }, [])
  );
  // Trust & Safety (Apple 1.2): report/block sheet target + a tick that forces a
  // re-render (and thus re-filter) whenever the device block list changes, so a
  // blocked author's posts vanish from the feed immediately.
  const [tsTarget, setTsTarget] = useState<ReportTarget | null>(null);
  const [, setBlockTick] = useState(0);
  useEffect(() => {
    const bump = () => setBlockTick((n) => n + 1);
    const unsubBlocked = onBlockedChange(bump);
    const unsubMuted = onMutedChange(bump);
    return () => { unsubBlocked(); unsubMuted(); };
  }, []);
  const [portfolioValue, setPortfolioValue] = useState<number | null>(null);
  // Story groups from GET /stories. Failure is silent — the row degrades to
  // just the "Add your story" button, exactly the pre-stories behavior.
  const [storyGroups, setStoryGroups] = useState<StoryGroup[]>([]);
  // IG-Notes-style status notes: my own note + friends' notes keyed by userId,
  // both from GET /notes. The "leave a note" input sheet is a small modal.
  const [myNote, setMyNote] = useState<StatusNote | undefined>(undefined);
  const [notesByUser, setNotesByUser] = useState<Record<string, StatusNote>>({});
  const [noteSheetOpen, setNoteSheetOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  // Discover-communities carousel + learn-preview rail (both live; empty on
  // failure so the rail hides rather than showing a broken strip).
  const [communities, setCommunities] = useState<CarouselCommunity[]>([]);
  const [learnArticles, setLearnArticles] = useState<LearnPreview[]>([]);
  // Discovery body (shown when the feed is empty): "Who to follow" people +
  // "Trending topics" hashtags, both derived from GET /trending. Empty on
  // failure so their sections simply hide rather than showing broken rows.
  const [suggestedUsers, setSuggestedUsers] = useState<SuggestedUser[]>([]);
  const [trendingTopics, setTrendingTopics] = useState<TrendingTopic[]>([]);

  // Scroll offset (reanimated) — drives the collapsing/overlapping Signals strip
  // in the footer so it shrinks out of the way as the feed scrolls up (skill 3-4:
  // continuous, value-driven, not a hard toggle). Visual only.
  const scrollY = useSharedValue(0);
  const scrollHandler = useAnimatedScrollHandler((e) => {
    scrollY.value = e.contentOffset.y;
  });
  const signalsCollapseStyle = useAnimatedStyle(() => {
    // Over the first 120px of scroll the strip shrinks + fades + lifts, then
    // stays collapsed so it never crowds the timeline. Height stays intact (it
    // lives below the feed); we scale/opacity/translate for the "overlap out"
    // feel without clipping neighboring footer modules.
    const p = interpolate(scrollY.value, [0, 120], [0, 1], Extrapolation.CLAMP);
    return {
      opacity: interpolate(p, [0, 1], [1, 0.55]),
      transform: [
        { translateY: interpolate(p, [0, 1], [0, -8]) },
        { scale: interpolate(p, [0, 1], [1, 0.92]) },
      ],
    };
  });
  // Frosted-header on scroll: at the very top the bar is fully transparent
  // (seamless with the gradient); as content scrolls under it a translucent
  // frosted tint + hairline ramps in over the first 56px so the header stays
  // legible and "doesn't get lost". No native blur dep — an animated tint.
  const headerFrostStyle = useAnimatedStyle(() => ({
    opacity: interpolate(scrollY.value, [0, 56], [0, 1], Extrapolation.CLAMP),
  }));

  // ── Data fetching ──
  // Monotonic fetch sequence: a response only applies if it's still the newest
  // request (filter switches / retries can't clobber fresher data), and the
  // loading watchdog can tell "this fetch cycle is still the live one".
  const fetchSeq = useRef(0);

  // Loads the feed for the given filter. Resolves the endpoint from FEED_TYPE_MAP
  // (or /posts/trending) and normalizes the envelope. Transient failures
  // auto-retry with backoff (MAX_FEED_RETRIES) before surfacing the retryable
  // error state — this was the July-1 "Couldn't load feed on a normal
  // connection" report: one failed round-trip flipped straight to the error UI.
  // api() itself hard-timeouts at 20s, so no attempt can hang forever.
  const feedEndpoint = useCallback((filter: string, pageNum: number) =>
    filter === 'Trending'
      ? `/posts/trending?limit=20&page=${pageNum}`
      : `/posts/feed?limit=20&page=${pageNum}&feedType=${FEED_TYPE_MAP[filter] ?? 'algorithmic'}`,
  []);

  const fetchPosts = useCallback(async (filter: string = activeFilter) => {
    const seq = ++fetchSeq.current;
    // A fresh first-page load resets pagination for the visible tab.
    loadingMoreRef.current = false;
    setLoadingMore(false);
    const endpoint = feedEndpoint(filter, 1);

    for (let attempt = 0; attempt <= MAX_FEED_RETRIES; attempt++) {
      let failed = false;
      try {
        const res = await api<{ items?: any[]; hasMore?: boolean }>(endpoint);
        if (seq !== fetchSeq.current) return; // a newer fetch owns the state now
        if (res.success && res.data) {
          // Sanitize: drop anything without an id so a malformed row can never
          // reach the list (belt; PostErrorBoundary is the suspenders).
          const items = (Array.isArray(res.data.items) ? res.data.items : [])
            .map(normalizePost)
            .filter((p) => p && typeof p.id === 'string' && p.id.length > 0 && !!p.user?.username);
          setPosts(items);
          setPage(1);
          // Trust the server's hasMore; fall back to "a full page came back" so a
          // short first page correctly stops paging.
          setHasMore(res.data.hasMore ?? items.length >= 20);
          setError(false);
          loadingRef.current = false;
          setLoading(false);
          return;
        }
        failed = true;
      } catch {
        failed = true;
      }
      if (seq !== fetchSeq.current) return;
      if (failed && attempt < MAX_FEED_RETRIES) {
        // Linear backoff (1.2s, 2.4s) before the next silent retry.
        await new Promise<void>((r) => setTimeout(r, FEED_RETRY_BASE_MS * (attempt + 1)));
        if (seq !== fetchSeq.current) return;
      }
    }

    // All attempts failed → retryable error state (never a misleading
    // "No posts yet", which is indistinguishable from a genuinely empty feed).
    setError(true);
    loadingRef.current = false;
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, feedEndpoint]);

  // Loads the NEXT page (onEndReached) and appends, de-duping by id (a live
  // prepend or overlapping page could otherwise duplicate a row). Guarded so
  // overlapping end-reached callbacks can't double-fetch; a single failed page
  // silently stops (no error banner mid-scroll) and can be retried by scrolling.
  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !hasMore || loading || error) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const seq = fetchSeq.current; // don't clobber a fresh reload in flight
    const nextPage = page + 1;
    try {
      const res = await api<{ items?: any[]; hasMore?: boolean }>(feedEndpoint(activeFilter, nextPage));
      if (seq !== fetchSeq.current) return; // a filter switch / refresh superseded us
      if (res.success && res.data) {
        const incoming = (Array.isArray(res.data.items) ? res.data.items : [])
          .map(normalizePost)
          .filter((p) => p && typeof p.id === 'string' && p.id.length > 0 && !!p.user?.username);
        setPosts((prev) => {
          const seen = new Set(prev.map((p) => p.id));
          return [...prev, ...incoming.filter((p) => !seen.has(p.id))];
        });
        setPage(nextPage);
        setHasMore(res.data.hasMore ?? incoming.length >= 20);
      }
    } catch {
      // Transient — leave hasMore as-is so scrolling again retries the page.
    } finally {
      if (seq === fetchSeq.current) setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [hasMore, loading, error, page, activeFilter, feedEndpoint]);

  // Loads the wallet portfolio total. `res.data ?? res` tolerates both the
  // wrapped ({ data: { totalValue } }) and bare response envelopes. A non-number
  // total resets to null so the card shows the em-dash placeholder.
  const fetchPortfolio = useCallback(async () => {
    try {
      const res = await api<any>('/wallet/portfolio');
      const d: any = res.data ?? res;
      setPortfolioValue(typeof d?.totalValue === 'number' ? d.totalValue : null);
    } catch {
      setPortfolioValue(null);
    }
  }, []);

  // Loads grouped stories for the story row. Errors keep the previous list
  // (or the initial []) so the row never breaks the feed header.
  const fetchStories = useCallback(async () => {
    try {
      const res = await api<StoryGroup[]>('/stories');
      if (res.success && Array.isArray(res.data)) {
        setStoryGroups(res.data);
      }
    } catch { /* non-fatal: keep whatever we had */ }
  }, []);

  // Loads IG-Notes status notes (GET /notes). Failure keeps whatever we had so
  // the note bubbles never break the story row.
  const fetchNotes = useCallback(async () => {
    try {
      const { myNote: mine, notesByUser: others } = await getNotes();
      setMyNote(mine ? { userId: mine.userId, text: mine.text } : undefined);
      setNotesByUser(
        Object.fromEntries(
          Object.entries(others).map(([id, n]) => [id, { userId: n.userId, text: n.text }]),
        ),
      );
    } catch { /* non-fatal: keep whatever we had */ }
  }, []);

  // Loads the discover-communities carousel (GET /communities). Backend wraps
  // the list as { data: { items } } and uses `icon` for the avatar — mirror the
  // CommunitiesScreen normalization. Failure leaves the rail hidden.
  const fetchCommunities = useCallback(async () => {
    try {
      const res = await api<{ items?: any[] }>('/communities');
      const items = res.success && Array.isArray(res.data?.items) ? res.data!.items : [];
      setCommunities(
        items
          .filter((c: any) => c && (c.name || c.id))
          .slice(0, 12)
          .map((c: any) => ({
            id: String(c.id ?? c.name),
            name: c.name,
            displayName: c.displayName,
            avatar: c.icon ?? c.avatar,
            memberCount: typeof c.memberCount === 'number' ? c.memberCount : undefined,
            joined: c.isJoined ?? c.joined ?? false,
          }))
      );
    } catch { /* non-fatal: rail hides */ }
  }, []);

  // Loads the learn-preview rail (GET /learn). Tolerates the articles/items/bare
  // envelope shapes the Learn screen already handles. Failure leaves it hidden.
  const fetchLearn = useCallback(async () => {
    try {
      const res = await api<any>('/learn?limit=12');
      const d: any = res.data ?? {};
      const list = d.articles ?? d.items ?? (Array.isArray(d) ? d : []);
      setLearnArticles(Array.isArray(list) ? list : []);
    } catch { /* non-fatal: rail hides */ }
  }, []);

  // Loads the discovery body's "Who to follow" people + "Trending topics"
  // hashtags from GET /trending (the same route SearchScreen's Trending column
  // uses: { users, posts, communities }). People come straight from `users`;
  // topics are hashtags harvested from trending post content. Only used to fill
  // the empty-feed discovery state, so failure just leaves those sections hidden.
  const fetchDiscovery = useCallback(async () => {
    try {
      const res = await api<any>('/trending?limit=25');
      const d: any = res.success ? res.data ?? {} : {};
      const rawUsers = Array.isArray(d.users) ? d.users : [];
      const users: SuggestedUser[] = rawUsers
        .map((raw: any) => {
          const u = raw?.user && typeof raw.user === 'object' ? raw.user : raw;
          const username = u?.username || u?.handle;
          if (!username) return null;
          const rawAvatar = u?.avatar ?? u?.avatarUrl ?? u?.avatar_url;
          return {
            id: String(u?.id ?? username),
            username: String(username),
            displayName:
              typeof u?.displayName === 'string' && u.displayName.length > 0
                ? u.displayName
                : String(username),
            avatar: typeof rawAvatar === 'string' && rawAvatar.length > 0 ? rawAvatar : undefined,
            bio:
              typeof u?.bio === 'string' && u.bio.length > 0
                ? u.bio.replace(/\s+/g, ' ').slice(0, 60)
                : undefined,
          } as SuggestedUser;
        })
        .filter((u: SuggestedUser | null): u is SuggestedUser => !!u)
        .slice(0, 8);
      setSuggestedUsers(users);

      // Harvest hashtags from trending post content → topic chips with a count.
      const rawPosts = Array.isArray(d.posts) ? d.posts : [];
      const counts = new Map<string, { tag: string; posts: number }>();
      for (const p of rawPosts) {
        const content = typeof p?.content === 'string' ? p.content : '';
        const tags = content.match(/#[\p{L}0-9_]{2,30}/gu) || [];
        for (const t of tags) {
          const key = t.toLowerCase();
          const prev = counts.get(key);
          if (prev) prev.posts += 1;
          else counts.set(key, { tag: t, posts: 1 });
        }
      }
      const topics: TrendingTopic[] = Array.from(counts.values())
        .sort((a, b) => b.posts - a.posts)
        .slice(0, 8);
      setTrendingTopics(topics);
    } catch { /* non-fatal: discovery sections hide */ }
  }, []);

  // Pull-to-refresh reloads feed, portfolio, stories, communities and learn.
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([
      fetchPosts(activeFilter),
      fetchPortfolio(),
      fetchStories(),
      fetchNotes(),
      fetchCommunities(),
      fetchLearn(),
      fetchDiscovery(),
    ]);
    setRefreshing(false);
  }, [fetchPosts, fetchPortfolio, fetchStories, fetchNotes, fetchCommunities, fetchLearn, fetchDiscovery, activeFilter]);

  // Refresh the feed when returning to Home — e.g. after creating a post in the
  // composer (which navigation.goBack()s here). Without this, a freshly-created
  // post didn't appear until a manual pull-to-refresh, which read as "posting does
  // nothing." Skip the very first focus; the filter effect already loads on mount.
  const didInitialFocus = useRef(false);
  useFocusEffect(
    useCallback(() => {
      if (!didInitialFocus.current) { didInitialFocus.current = true; return; }
      fetchPosts(activeFilter);
      fetchPortfolio();
      // Stories refresh on focus too — returning from the viewer should flip
      // rings to "viewed", and returning from create should show the new story.
      fetchStories();
      // Notes refresh on focus so a friend's newly-posted note appears + my own
      // note reflects any change made elsewhere.
      fetchNotes();
    }, [fetchPosts, fetchPortfolio, fetchStories, fetchNotes, activeFilter])
  );

  // ── Effects ──
  // Reload the feed whenever the active filter changes (shows the spinner each
  // time). fetchPosts is intentionally omitted from deps to avoid a
  // double-fetch on its identity change. The watchdog guarantees the spinner
  // NEVER runs past FEED_LOADING_WATCHDOG_MS — it flips to the friendly
  // error + Retry instead (a still-in-flight fetch that later succeeds simply
  // lands and replaces the error state).
  useEffect(() => {
    loadingRef.current = true;
    setLoading(true);
    fetchPosts(activeFilter);
    const watchdog = setTimeout(() => {
      if (loadingRef.current) {
        loadingRef.current = false;
        setLoading(false);
        setError(true);
      }
    }, FEED_LOADING_WATCHDOG_MS);
    return () => clearTimeout(watchdog);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  // Portfolio, stories, communities and learn load once on mount (independent
  // of the feed filter).
  useEffect(() => {
    fetchPortfolio();
    fetchStories();
    fetchNotes();
    fetchCommunities();
    fetchLearn();
    fetchDiscovery();
  }, [fetchPortfolio, fetchStories, fetchNotes, fetchCommunities, fetchLearn, fetchDiscovery]);

  // ── Live feed events (LR3) ──
  // Start the socket→feed bridge (idempotent) and prepend new posts live without
  // a full refetch. A new post from a followed author (server `feed:post`) or the
  // user's own freshly-composed post both arrive via onNewFeedPost (deduped by id
  // in feedEvents). We only prepend on the "For You"/"Following"/"New" feeds — a
  // live post doesn't belong injected into the Trending ranking.
  useEffect(() => {
    startFeedEvents();
    const unsub = onNewFeedPost((raw) => {
      if (activeFilter === 'Trending') return;
      const np = normalizePost(raw);
      if (!np.id || typeof np.id !== 'string' || !np.user?.username) return;
      setPosts((prev) => {
        if (prev.some((p) => p.id === np.id)) return prev; // already present
        return [np, ...prev];
      });
    });
    return unsub;
  }, [activeFilter]);

  // On a non-recovered reconnect (long background / cold socket), the live
  // `feed:post` stream had a gap — refetch the current feed so nothing is missed.
  // A recovered reconnect replayed events, so no refetch is needed.
  useEffect(() => {
    return onReconnect(({ recovered }) => {
      if (!recovered) fetchPosts(activeFilter);
    });
  }, [fetchPosts, activeFilter]);

  // Fetch current user avatar for the story row.
  useEffect(() => {
    (async () => {
      try {
        const res = await api<{ user?: any }>('/auth/me');
        const me = res.success ? res.data?.user ?? res.data : null;
        if (me) {
          setMeAvatar(me.avatar ?? me.avatar_url ?? undefined);
          setMeDisplayName(me.displayName ?? me.display_name ?? me.username ?? undefined);
          setMeId(me.id != null ? String(me.id) : undefined);
          setMeUsername(me.username ?? undefined);
        }
      } catch { /* non-fatal */ }
    })();
  }, []);

  // ── Notes (IG-Notes) handlers ──
  // Tap my own bubble → open the input sheet seeded with my current note.
  const handleLeaveNote = useCallback(() => {
    setNoteDraft(myNote?.text ?? '');
    setNoteSheetOpen(true);
  }, [myNote]);

  // Save the draft as my note (create/replace). Optimistically reflect it, then
  // reconcile from the server so expiry/id are authoritative.
  const handleSaveNote = useCallback(async () => {
    const text = noteDraft.trim();
    if (!text || noteSaving) return;
    setNoteSaving(true);
    const saved = await apiSetNote(text);
    if (saved) {
      setMyNote({ userId: saved.userId, text: saved.text });
      setNoteSheetOpen(false);
      setNoteDraft('');
    } else {
      Alert.alert('Could not save note', 'Please try again.');
    }
    setNoteSaving(false);
    fetchNotes();
  }, [noteDraft, noteSaving, fetchNotes]);

  // Clear my note.
  const handleClearNote = useCallback(async () => {
    setNoteSaving(true);
    const ok = await apiClearNote();
    if (ok) {
      setMyNote(undefined);
      setNoteSheetOpen(false);
      setNoteDraft('');
    } else {
      Alert.alert('Could not clear note', 'Please try again.');
    }
    setNoteSaving(false);
  }, []);

  // Tap a friend's note bubble → surface it (simple alert view for v1).
  const handleOpenNote = useCallback((userId: string) => {
    const n = notesByUser[userId];
    if (!n?.text) return;
    Alert.alert('Note', n.text);
  }, [notesByUser]);

  // ── Stable list callbacks (memoization) ──
  // Stable handlers so memoized PostCard rows don't re-render on every parent
  // render. onPress/onOptions are looked up per-item by id.
  const handleOpenPost = useCallback((id: string) => navigation.navigate('Post', { id }), [navigation]);

  // Delete the viewer's OWN post: DELETE /posts/:id, then remove it from the list
  // optimistically. On failure surface a non-silent Alert (the row stays put).
  const handleDeletePost = useCallback(async (item: Post) => {
    if (deletingPostRef.current[item.id]) return;
    deletingPostRef.current[item.id] = true;
    // Optimistically drop the row; keep a snapshot to restore on failure.
    let removed: Post | undefined;
    let removedIndex = -1;
    setPosts((prev) => {
      removedIndex = prev.findIndex((p) => p.id === item.id);
      if (removedIndex >= 0) removed = prev[removedIndex];
      return prev.filter((p) => p.id !== item.id);
    });
    const res = await api(`/posts/${item.id}`, { method: 'DELETE' });
    if (!res.success) {
      // Restore the row at its original position.
      if (removed) {
        setPosts((prev) => {
          if (prev.some((p) => p.id === item.id)) return prev;
          const next = [...prev];
          next.splice(Math.max(0, removedIndex), 0, removed!);
          return next;
        });
      }
      if (!(res as any).silent) {
        Alert.alert('Couldn’t delete post', (res as any).error || 'Please try again.');
      }
    }
    deletingPostRef.current[item.id] = false;
  }, []);

  const confirmDeletePost = useCallback((item: Post) => {
    Alert.alert(
      'Delete post?',
      'This can’t be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: () => handleDeletePost(item) },
      ],
      { cancelable: true }
    );
  }, [handleDeletePost]);

  // Post kebab. The AUTHOR gets a "Delete post" action (reporting/blocking
  // yourself is meaningless); everyone else gets the Apple 1.2 report/block sheet.
  const handleOpenOptions = useCallback((item: Post) => {
    const isAuthor =
      (!!meId && !!item.user?.id && meId === item.user.id) ||
      (!!meUsername && !!item.user?.username &&
        meUsername.toLowerCase() === item.user.username.toLowerCase());
    if (isAuthor) {
      Alert.alert(
        'Post options',
        undefined,
        [
          { text: 'Delete post', style: 'destructive', onPress: () => confirmDeletePost(item) },
          { text: 'Cancel', style: 'cancel' },
        ],
        { cancelable: true }
      );
      return;
    }
    setTsTarget({
      type: 'post',
      contentId: item.id,
      authorId: item.user?.id,
      authorUsername: item.user?.username,
      authorLabel: `@${item.user?.username || 'user'}`,
    });
  }, [meId, meUsername, confirmDeletePost]);
  // Stable key: prefer the server id; on the (post-filter, near-impossible) miss
  // fall back to a content+user composite so the key is still stable across
  // re-renders/reorders — never a bare index (which reuses keys as rows shift).
  const keyExtractor = useCallback((item: Post, index: number) => {
    if (item?.id && typeof item.id === 'string') return item.id;
    return `post-${item?.user?.username ?? 'anon'}-${item?.created_at ?? ''}-${(item?.content ?? '').slice(0, 24)}-${index}`;
  }, []);
  const renderItem = useCallback(
    ({ item }: { item: Post }) => (
      // Pass the STABLE parent callbacks straight through — PostCard binds them to
      // its own post internally — so the memo comparator holds and rows don't
      // re-render on every parent render / live prepend. The boundary means one
      // malformed post drops just its own row, never the whole screen.
      <PostErrorBoundary>
        <PostCard post={item} onPress={handleOpenPost} onOptions={handleOpenOptions} />
      </PostErrorBoundary>
    ),
    [handleOpenPost, handleOpenOptions]
  );

  // ── Render ──
  // Header rendered above the feed: stories, portfolio, filter pills. Portfolio
  // actions route to their respective crypto/wallet surfaces.
  // The timeline LEADS (skill 16 — strip the crypto dashboard from the top):
  // just the live-session banner, the story strip, then the sticky segmented
  // tabs, and immediately the posts. The crypto rails (signals / communities /
  // portfolio) are DEMOTED into the footer as discovery modules — kept, not
  // deleted (SHOW_CRYPTO still gates the portfolio card).
  const ListHeader = () => (
    <View>
      {/* X-style live-session banner at the very top of the feed. Self-fetches
          the busiest active voice room (renders nothing when none are live) and
          taps straight into that room. */}
      <LiveSessionBanner
        onPress={(id) => navigation.navigate('VoiceRoom', { id })}
      />
      {/* "Communities to join" — horizontal rounded chips (icon + name + Join),
          replacing the old stories circle row (client's explicit ask). Chip body
          taps into the Community; the Join button follows it in place. */}
      <JoinCommunitiesRow
        communities={communities}
        onOpen={(name) => navigation.navigate('Community', { name })}
        onSeeAll={() => navigation.navigate('Communities')}
      />
      {/* Learning Center rail surfaced ABOVE the feed (was footer-only) — Home now
          leads with Communities + Learn before the feed → a more introductory,
          "what CRYB is" first screen (client ask). */}
      <LearnRail
        articles={learnArticles}
        onOpen={(slug) => navigation.navigate('LearnArticle', { slug })}
        onSeeAll={() => navigation.navigate('LearnIndex')}
        layout={layout}
      />
      {/* Sticky x.com-style sub-header: For You / Following / Trending / New. */}
      <SegmentedTabs active={activeFilter} onSelect={setActiveFilter} />
      {/* Promo "Discover live rooms" banner removed per feedback — the feed goes
          straight from the tabs into posts, like major social apps. */}
    </View>
  );

  // Discovery modules sit BELOW the feed (ListFooter): the crypto rails demoted
  // out of the top, then the learn rail. This is the "who to follow"/interstitial
  // pattern — present but no longer making Home read as a crypto dashboard.
  const ListFooter = () => (
    <View>
      {/* Pagination footer: a spinner while the next page loads, then a subtle
          "end of feed" marker once there are no more pages (only when we actually
          have posts, so it never shows on an empty/error feed). */}
      {loadingMore ? (
        <View style={styles.pagingFooter}>
          <ActivityIndicator size="small" color={colors.primary} />
        </View>
      ) : !hasMore && posts.length > 0 ? (
        <View style={styles.pagingFooter}>
          <Text style={styles.pagingEndText}>You're all caught up</Text>
        </View>
      ) : null}
      {/* New-user onboarding checklist — self-hides once dismissed or complete. */}
      <GetStartedCard />
      {/* (LearnRail moved to the header — Home now leads with it.) */}
      {/* Discover-communities carousel (live). */}
      <CommunitiesCarousel
        communities={communities}
        onOpen={(name) => navigation.navigate('Community', { name })}
        onSeeAll={() => navigation.navigate('Communities')}
        layout={layout}
      />
      {/* SMALL crypto/v2 signals strip — live market data, gated as coming-in-v2.
          Demoted below the feed and made much smaller; it collapses/overlaps out
          of the way as the feed scrolls up (signalsCollapseStyle). */}
      <Animated.View style={[styles.signalsCollapse, signalsCollapseStyle]}>
        <SignalsRow />
      </Animated.View>
      {SHOW_CRYPTO && (
        <PortfolioCard value={portfolioValue} onAction={(label) => {
          if (label === 'Swap') navigation.navigate('Crypto', { screen: 'Swap' });
          // The Tabs "Markets" slot renders Communities in v1 — the real markets
          // hub lives on its own root route.
          else if (label === 'Markets') navigation.navigate('MarketsHub');
          else if (label === 'Portfolio') navigation.navigate('Portfolio');
          else if (label === 'Wallet') navigation.navigate('Wallet');
        }} />
      )}
    </View>
  );

  // Floating header height reserved as top inset for the feed (avatar 32 +
  // vertical padding ≈ 56) plus the safe-area notch. Both the FlatList and the
  // loading spinner pad below this so content scrolls UNDER the glass bar
  // instead of the bar consuming a fixed strip (skill 12).
  // Tightened (was +56) so the first section sits closer under the wordmark —
  // the loose gap the feedback flagged. Still clears the 32pt logo + its padding.
  const headerHeight = insets.top + 48;

  return (
    <View style={styles.container}>
      <ParticleBackground />
      {/* Header blends into the background gradient (client: no white bar). A soft
          bg→transparent scrim sits behind the wordmark + icons — seamless with the
          ParticleBackground at the top, and it softens feed content scrolling under. */}
      <GlassBar
        radius={0}
        surfaceColor="transparent"
        style={[styles.headerBar, styles.headerTransparent]}
        contentStyle={[
          styles.header,
          { paddingTop: insets.top },
        ]}
      >
        <LinearGradient
          colors={[colors.bg, colors.bg + 'E6', colors.bg + '00']}
          locations={[0, 0.55, 1]}
          style={StyleSheet.absoluteFill}
          pointerEvents="none"
        />
        {/* Frosted tint that ramps in on scroll (headerFrostStyle) so the bar
            stays legible once content slides under it. */}
        <Animated.View style={[styles.headerFrost, headerFrostStyle]} pointerEvents="none" />
        <View style={[styles.headerInner, maxContentWidth ? { maxWidth: maxContentWidth, alignSelf: 'center' as const, width: '100%' } : undefined]}>
          <View style={styles.headerLeft}>
            <TouchableOpacity
              onPress={() => openDrawer()}
              accessibilityRole="button"
              accessibilityLabel="Open menu"
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              {typeof meAvatar === 'string' && meAvatar.length > 0 ? (
                <Image source={{ uri: meAvatar }} style={styles.headerAvatar} />
              ) : (
                <View style={[styles.headerAvatar, styles.headerAvatarFallback]}>
                  <Ionicons name="person" size={16} color={colors.primary} />
                </View>
              )}
            </TouchableOpacity>
            {/* CRYB wordmark. DEMOTED from a glass pill to a plain View — the
                logo now sits ON the GlassBar and skill 12 forbids stacking a
                light translucent surface on another (legibility collapses). */}
            <View style={styles.logoGlassInner}>
              <LogoWordmark size={26} />
            </View>
          </View>
          <View style={styles.headerRight}>
            <TouchableOpacity
              style={styles.headerIcon}
              onPress={() => navigation.navigate('Search', { focusSearch: true })}
              accessibilityRole="button"
              accessibilityLabel="Search"
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Ionicons name="search-outline" size={22} color={colors.ink} />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.headerIcon}
              onPress={() => navigation.navigate('Notifications')}
              accessibilityRole="button"
              accessibilityLabel="Notifications"
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Ionicons name="notifications-outline" size={22} color={colors.ink} />
            </TouchableOpacity>
          </View>
        </View>
      </GlassBar>

      {loading ? (
        <View style={[styles.loadingWrap, { paddingTop: headerHeight }]}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <AnimatedFlatList
          data={(Array.isArray(posts) ? posts : [])
            .filter((p) => !containsMutedWord(p?.content, mutedWords))
            .filter((p) => !(isBlocked(p?.user?.id, p?.user?.username) || isMuted(p?.user?.id, p?.user?.username)))}
          keyExtractor={keyExtractor}
          renderItem={renderItem}
          onScroll={scrollHandler}
          scrollEventThrottle={16}
          // Cursor/offset paging: fetch the next page as the user nears the end.
          onEndReached={loadMore}
          onEndReachedThreshold={0.5}
          // Windowing perf: posts are variable-height (wrapped text) so a fixed
          // getItemLayout would clip — instead tune the render window so the list
          // never thrashes/blanks and new prepended items don't force a full pass.
          removeClippedSubviews
          initialNumToRender={8}
          maxToRenderPerBatch={8}
          windowSize={11}
          ListHeaderComponent={<Safe label="HomeHeader"><ListHeader /></Safe>}
          ListFooterComponent={ListFooter}
          ListEmptyComponent={
            error ? (
              <View style={styles.emptyCard}>
                <Asset3D name="error" size={128} style={{ marginBottom: spacing.md }} />
                <Text style={styles.emptyText}>Couldn't load feed</Text>
                <Text style={styles.emptySubtext}>
                  Check your connection and try again.
                </Text>
                <TouchableOpacity
                  style={styles.emptyCtaBtn}
                  onPress={() => {
                    // Fresh attempt cycle: reset the error, restart the spinner
                    // (bounded by the watchdog), and refetch with a new backoff
                    // budget.
                    setError(false);
                    loadingRef.current = true;
                    setLoading(true);
                    fetchPosts(activeFilter);
                    setTimeout(() => {
                      if (loadingRef.current) {
                        loadingRef.current = false;
                        setLoading(false);
                        setError(true);
                      }
                    }, FEED_LOADING_WATCHDOG_MS);
                  }}
                  activeOpacity={0.8}
                >
                  <Ionicons name="refresh-outline" size={16} color={colors.white} />
                  <Text style={styles.emptyCtaText}>Retry</Text>
                </TouchableOpacity>
              </View>
            ) : (
              // A genuinely empty feed gets a CLEAN, tab-aware empty state — one
              // icon, one line, one CTA (never a dead end, never onboarding/
              // connect rows interleaved into a content tab). Discovery lives on
              // the Discover tab, not stuffed into an empty Home feed.
              <FeedEmptyState
                filter={activeFilter}
                onPrimary={() => {
                  if (activeFilter === 'Following') navigation.navigate('Search');
                  else if (activeFilter === 'Trending' || activeFilter === 'New') onRefresh();
                  else navigation.navigate('CreatePost');
                }}
              />
            )
          }
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={colors.primary}
            />
          }
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[
            // Top inset so the first feed item clears the floating glass header
            // (the header no longer reserves layout space — content scrolls
            // under it, skill 12).
            { paddingTop: headerHeight },
            // Device-exact clearance for the floating glass tab bar (safe-area +
            // bar body + gap) so the last post is never hidden behind it.
            { paddingBottom: tabBarClearance },
            maxContentWidth ? { maxWidth: maxContentWidth, alignSelf: 'center' as const, width: '100%' } : undefined,
          ]}
        />
      )}
      <ReportBlockSheet
        visible={!!tsTarget}
        target={tsTarget}
        onClose={() => setTsTarget(null)}
        onBlocked={() => { setTsTarget(null); /* feed re-filters via onBlockedChange */ }}
      />

      {/* IG-Notes "leave a note" input sheet. A lightweight bottom sheet: type a
          short note (280 max), Share to post/replace it, or Clear to remove it. */}
      <Modal
        visible={noteSheetOpen}
        transparent
        animationType="slide"
        onRequestClose={() => setNoteSheetOpen(false)}
      >
        <TouchableOpacity
          style={styles.noteSheetBackdrop}
          activeOpacity={1}
          onPress={() => setNoteSheetOpen(false)}
        >
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}
            style={styles.noteSheetKav}
          >
            <TouchableOpacity activeOpacity={1} style={styles.noteSheet}>
              <View style={styles.noteSheetHandle} />
              <Text style={styles.noteSheetTitle}>Leave a note</Text>
              <Text style={styles.noteSheetHint}>
                Share a thought with friends. Notes disappear after 24 hours.
              </Text>
              <TextInput
                value={noteDraft}
                onChangeText={setNoteDraft}
                placeholder="What's on your mind?"
                placeholderTextColor={colors.muted}
                style={styles.noteInput}
                maxLength={280}
                multiline
                autoFocus
                accessibilityLabel="Note text"
              />
              <View style={styles.noteSheetActions}>
                {!!myNote?.text && (
                  <TouchableOpacity
                    style={[styles.noteBtn, styles.noteBtnGhost]}
                    onPress={handleClearNote}
                    disabled={noteSaving}
                    accessibilityRole="button"
                    accessibilityLabel="Clear my note"
                  >
                    <Text style={[styles.noteBtnText, { color: colors.primary }]}>Clear</Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity
                  style={[styles.noteBtn, (!noteDraft.trim() || noteSaving) && styles.noteBtnDisabled]}
                  onPress={handleSaveNote}
                  disabled={!noteDraft.trim() || noteSaving}
                  accessibilityRole="button"
                  accessibilityLabel="Share note"
                >
                  {noteSaving ? (
                    <ActivityIndicator size="small" color={colors.white} />
                  ) : (
                    <Text style={styles.noteBtnText}>Share</Text>
                  )}
                </TouchableOpacity>
              </View>
            </TouchableOpacity>
          </KeyboardAvoidingView>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    // No opaque fill: ParticleBackground (absoluteFill) is the sole top-to-bottom
    // surface so the aurora bleeds full-screen under the notch (skill 12/16).
    flex: 1,
  },
  // Floating glass header shell: pinned to the very top edge, edge-to-edge, feed
  // scrolls underneath. zIndex keeps it above the FlatList.
  headerBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
  // Seamless with the background gradient: no glass fill/border/shadow (a bg→
  // transparent scrim child provides the blend). Client: "no white bar header".
  headerTransparent: {
    borderWidth: 0,
    borderBottomWidth: 0,
    shadowOpacity: 0,
    elevation: 0,
    backgroundColor: 'transparent',
  },
  // Frosted tint layer for the scroll-in header (opacity animated 0→1). A soft
  // translucent white with a hairline bottom border — reads as frosted glass
  // gaining presence as content scrolls under it.
  headerFrost: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255,255,255,0.55)',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.cardBorder,
  },
  header: {
    // Inner content padding of the GlassBar (paddingTop is applied inline with
    // insets.top). Tighter bottom padding pulls the wordmark nearer the top edge.
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.xs,
  },
  headerInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2, // CRYB pegado al avatar (era spacing.sm → mucho espacio)
  },
  // CRYB wordmark holder (plain View — demoted from glass, see header).
  logoGlassInner: {
    paddingHorizontal: 0, // sin padding lateral → el wordmark queda junto al avatar
    paddingVertical: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  headerAvatarFallback: {
    backgroundColor: colors.primary + '20',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  headerIcon: {
    padding: spacing.xs,
  },
  storyRow: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    gap: spacing.md,
  },
  storyItem: {
    alignItems: 'center',
    width: 64,
  },
  storyRing: {
    width: 56,
    height: 56,
    borderRadius: 28,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 2,
  },
  // Gradient ring for fetched story circles: a 56pt gradient disc with a
  // bg-colored 52pt inner circle, leaving a 2pt ring + 2pt gap around the
  // 48pt avatar — visually matches the dashed "Add" ring dimensions.
  storyGradientRing: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  storyRingInner: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: colors.bg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  storyAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  storyInitial: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.primary,
  },
  storyAddBadge: {
    position: 'absolute',
    bottom: 14,
    right: 2,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.bg,
  },
  storyLabel: {
    fontSize: 11,
    color: colors.ink2,
    marginTop: 4,
    fontWeight: '500',
  },
  // ── "Communities to join" strip (replaces the stories circle row) ──
  joinRowSection: {
    paddingTop: 0,
    paddingBottom: spacing.sm,
  },
  joinRowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.sm,
  },
  joinRowTitle: {
    ...typographyScale.body,
    fontWeight: '700',
    color: colors.ink,
    letterSpacing: -0.2,
  },
  joinRow: {
    paddingHorizontal: spacing.lg,
    gap: spacing.sm,
  },
  joinCard: {
    width: 124,
    overflow: 'hidden',
  },
  joinCardBody: {
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
    alignItems: 'center',
    gap: spacing.xs,
  },
  joinCardAvatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: colors.primary + '14',
    borderWidth: 1,
    borderColor: colors.cardBorder,
    marginBottom: 2,
  },
  joinCardName: {
    ...typographyScale.caption,
    fontWeight: '700',
    color: colors.ink,
    textAlign: 'center',
    maxWidth: '100%',
  },
  joinCardBtn: {
    alignSelf: 'stretch',
    backgroundColor: colors.primary,
    borderRadius: radius.full,
    paddingVertical: 7,
    alignItems: 'center',
    justifyContent: 'center',
  },
  joinCardBtnActive: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.cardBorder,
  },
  joinChipBtnText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  joinChipBtnTextActive: {
    color: colors.ink,
  },
  // ── Pinned announcement / live bar (slim x.com pinned row) ──
  // Collapsing wrapper for the demoted footer Signals strip.
  signalsCollapse: {
    marginTop: spacing.xs,
  },
  portfolio: {
    marginHorizontal: spacing.lg,
    marginVertical: spacing.sm,
  },
  portfolioLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  portfolioLabel: {
    ...typographyScale.caption,
    color: colorTokens.light.textMuted,
  },
  portfolioValue: {
    ...typographyScale.h1,
    color: colorTokens.light.text,
    marginBottom: spacing.md,
  },
  portfolioActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  portfolioAction: {
    alignItems: 'center',
    gap: 4,
    minWidth: 44,
    minHeight: 44,
    justifyContent: 'center',
  },
  portfolioIconWrap: {
    width: 44,
    height: 44,
    borderRadius: radius.full,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  portfolioActionLabel: {
    ...typographyScale.caption,
    color: colorTokens.light.textMuted,
  },
  // ── Segmented tabs (x.com sub-header) — reuses ProfileScreen's underline tab
  // pattern (skill 7) with a sliding reanimated indicator. ──
  segTabs: {
    flexDirection: 'row',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.cardBorder,
    position: 'relative',
  },
  segTab: {
    flex: 1,
    paddingVertical: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  segTabLabel: {
    ...typographyScale.body,
    fontWeight: '500',
    color: colors.muted,
  },
  segTabLabelActive: {
    fontWeight: '700',
    color: colors.ink,
  },
  segIndicator: {
    position: 'absolute',
    bottom: -StyleSheet.hairlineWidth,
    left: 0,
    height: 2,
    backgroundColor: colors.primary,
    borderRadius: 1,
  },
  // Timeline post row: edge-to-edge, separated by a single hairline — the dense
  // "timeline" feel (not a floating rounded card).
  postCard: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.cardBorder,
  },
  // In-post media (v2-gated). Single image = one contained 16:9 card; multiple =
  // a centered 2-up grid. Always centered.
  postMediaWrap: {
    marginTop: spacing.sm,
    alignSelf: 'center',
    width: '100%',
  },
  postMediaSingle: {
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: radius.md,
    alignSelf: 'center',
  },
  postMediaGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    justifyContent: 'center',
  },
  postMediaCell: {
    width: '49%',
    aspectRatio: 1,
    borderRadius: radius.md,
  },
  postHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  postAvatarWrap: {
    marginRight: spacing.sm,
  },
  postAvatar: {
    width: 38,
    height: 38,
    borderRadius: 19,
  },
  postAvatarFallback: {
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
  },
  postAvatarLetter: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.primary,
  },
  postMeta: {
    flex: 1,
  },
  postName: {
    ...typographyScale.body,
    fontWeight: '600',
    color: colors.ink,
    letterSpacing: -0.2,
  },
  postHandle: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 1,
  },
  postContent: {
    ...typographyScale.body,
    color: colors.ink,
    marginTop: spacing.sm,
  },
  postActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: spacing.xs,
    paddingTop: spacing.xs,
  },
  // Left cluster (like/comment/repost) + right cluster (save/share).
  postActionsGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xl,
  },
  postActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    minHeight: 40,
    paddingVertical: spacing.xs,
  },
  postActionCount: {
    ...typographyScale.caption,
    color: colors.muted,
  },
  loadingWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Pagination footer (next-page spinner / end-of-feed marker).
  pagingFooter: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.lg,
  },
  pagingEndText: {
    fontSize: 13,
    color: colors.muted,
    ...fonts.regular,
  },
  // Glass empty-state card — matches the discover-page treatment instead of
  // floating bare text on the background. Fill/border mirror the GlassCard
  // (sheet-grade opacity so text stays legible) and elevation reads from the
  // shared shadowTokens.card so it lifts on-brand.
  emptyCard: {
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: spacing.lg,
    marginTop: spacing.xl,
    paddingVertical: spacing.xxxl,
    paddingHorizontal: spacing.xl,
    borderRadius: radius.xl,
    gap: spacing.sm,
    // Dark glass (was a white slab). Elevated card surface + hairline border so
    // the error state reads on-brand over the aurora, never as a white block.
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    ...shadowTokens.card,
  },
  emptyIconWrap: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: colors.primary + '14',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.xs,
  },
  emptyCtaGhost: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.primary,
    marginTop: spacing.sm,
  },
  emptyText: {
    ...typographyScale.h3,
    color: colors.ink,
    textAlign: 'center',
  },
  emptySubtext: {
    ...typographyScale.caption,
    color: colors.muted,
    textAlign: 'center',
  },
  emptyCtaBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm + 2,
    borderRadius: radius.full,
    marginTop: spacing.md,
    gap: spacing.xs,
    minHeight: 44,
  },
  emptyCtaText: {
    ...typographyScale.body,
    fontWeight: '600',
    color: colors.white,
  },

  // ── Discovery rails (communities + learn) ──
  railSection: {
    marginTop: spacing.md,
  },
  railHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.sm,
  },
  railHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs + 2,
  },
  railTitle: {
    ...typographyScale.h3,
    color: colors.ink,
  },
  railSeeAll: {
    ...typographyScale.caption,
    color: colors.primary,
    ...fonts.semibold,
  },
  railRow: {
    paddingHorizontal: spacing.lg,
    gap: spacing.sm + 2,
  },

  // Community card
  communityCard: {
    width: 128,
    alignItems: 'center',
  },
  communityAvatar: {
    width: 52,
    height: 52,
    borderRadius: radius.full,
  },
  communityAvatarFallback: {
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
  },
  communityInitial: {
    ...typographyScale.h3,
    color: colors.primary,
  },
  communityName: {
    ...typographyScale.body,
    fontWeight: '600',
    color: colors.ink,
    marginTop: spacing.sm,
    textAlign: 'center',
  },
  communityMeta: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 1,
    textAlign: 'center',
  },

  // Learn card
  learnCard: {
    width: 200,
    overflow: 'hidden',
  },
  learnImage: {
    width: '100%',
    height: 96,
  },
  learnImageFallback: {
    backgroundColor: colors.primary + '14',
    alignItems: 'center',
    justifyContent: 'center',
  },
  learnBody: {
    padding: spacing.md,
  },
  learnCategory: {
    fontSize: 9,
    letterSpacing: 0.8,
    color: colors.primary,
    ...fonts.bold,
    marginBottom: 4,
  },
  learnTitle: {
    ...typographyScale.body,
    fontWeight: '600',
    color: colors.ink,
  },
  // ── Empty-feed discovery body ──
  discoveryBody: {
    paddingTop: spacing.sm,
  },
  discoverySection: {
    marginTop: spacing.lg,
  },
  // "Start a conversation" composer launcher.
  composerCard: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: spacing.lg,
    gap: spacing.md,
  },
  composerAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  composerAvatarFallback: {
    backgroundColor: colors.primary + '20',
    alignItems: 'center',
    justifyContent: 'center',
  },
  composerPlaceholder: {
    ...typographyScale.body,
    color: colors.muted,
    flex: 1,
  },
  composerBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Grouped "Who to follow" card + rows.
  discoveryCard: {
    marginHorizontal: spacing.lg,
    overflow: 'hidden',
  },
  discoveryDivider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.cardBorder,
    marginLeft: spacing.lg + 44 + spacing.md,
  },
  suggestRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    gap: spacing.md,
  },
  suggestAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
  },
  suggestAvatarFallback: {
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
  },
  suggestInitial: {
    ...typographyScale.h3,
    color: colors.primary,
  },
  suggestMeta: {
    flex: 1,
  },
  suggestName: {
    ...typographyScale.body,
    fontWeight: '600',
    color: colors.ink,
  },
  suggestHandle: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 1,
  },
  followBtn: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radius.full,
    backgroundColor: colors.primary,
    minHeight: 34,
    alignItems: 'center',
    justifyContent: 'center',
  },
  followBtnActive: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.cardBorder,
  },
  followBtnText: {
    ...typographyScale.caption,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  followBtnTextActive: {
    color: colors.ink,
  },
  // Inline follow error (real server message, e.g. "Already following") — sits
  // just under the row, aligned with the name column, in the error tint.
  followError: {
    ...typographyScale.caption,
    color: colors.error,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.sm,
    marginTop: -spacing.xs,
  },
  // Trending-topic chips.
  topicWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: spacing.lg,
    gap: spacing.sm,
  },
  topicChip: {
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    borderRadius: radius.full,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  topicTag: {
    ...typographyScale.body,
    fontWeight: '600',
    color: colors.ink,
  },
  topicCount: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 1,
  },

  // ── IG-Notes "leave a note" input sheet ──
  noteSheetBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  noteSheetKav: {
    width: '100%',
  },
  noteSheet: {
    backgroundColor: colors.card,
    borderTopLeftRadius: radius.xl,
    borderTopRightRadius: radius.xl,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xl,
  },
  noteSheetHandle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.faint,
    marginBottom: spacing.md,
  },
  noteSheetTitle: {
    ...typographyScale.h3,
    color: colors.ink,
    marginBottom: 4,
  },
  noteSheetHint: {
    ...typographyScale.caption,
    color: colors.muted,
    marginBottom: spacing.md,
  },
  noteInput: {
    minHeight: 72,
    maxHeight: 140,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.faint,
    backgroundColor: colors.bg,
    color: colors.ink,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    ...typographyScale.body,
    textAlignVertical: 'top',
  },
  noteSheetActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  noteBtn: {
    minWidth: 88,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderRadius: radius.full,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  noteBtnGhost: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.primary,
  },
  noteBtnDisabled: {
    opacity: 0.5,
  },
  noteBtnText: {
    ...typographyScale.body,
    fontWeight: '700',
    color: colors.white,
  },
});
